import { useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Download, Loader2, Printer } from "lucide-react";

// Offered label sizes — must match backend shipping_label_service.LABEL_SIZES.
// Shared with the orders-list bulk label download.
export const LABEL_SIZES = [
  { key: "4x6", label: '4×6"' },
  { key: "A4", label: "A4" },
  { key: "A5", label: "A5" },
];

const errDetail = (e, fb) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg;
  return e?.response?.data?.message || fb;
};

/**
 * White-label shipping-label download with a size picker. First click for a size
 * generates + caches the PDF in S3 (backend); subsequent clicks reuse it. Opens the
 * (presigned) PDF in a new tab.
 *
 * @param apiBase "b2c/admin" | "b2c/seller" — the portal's order route base.
 * @param orderId the order's `id` (not the URL param).
 */
export default function ShippingLabelDownload({ apiBase, orderId, className = "" }) {
  const [fetching, setFetching] = useState(null);

  const download = async (size) => {
    setFetching(size);
    try {
      const { data } = await axios.get(
        `${API}/${apiBase}/orders/${orderId}/shipping-label?size=${encodeURIComponent(size)}`
      );
      if (data?.url) {
        window.open(data.url, "_blank", "noopener,noreferrer");
        if (data.cached === false) toast.success(`${size} shipping label generated`);
      } else {
        toast.error("Label link unavailable — please retry");
      }
    } catch (e) {
      toast.error(errDetail(e, "Could not generate the shipping label"));
    } finally {
      setFetching(null);
    }
  };

  return (
    <div className={className}>
      <p className="text-sm font-medium text-slate-700 flex items-center gap-1.5 mb-2">
        <Printer className="w-4 h-4 text-brand" /> Shipping label
      </p>
      <div className="grid grid-cols-3 gap-2">
        {LABEL_SIZES.map((s) => (
          <Button
            key={s.key}
            variant="outline"
            size="sm"
            disabled={!!fetching}
            onClick={() => download(s.key)}
            data-testid={`b2c-label-${s.key}`}
          >
            {fetching === s.key ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-1.5" />
            )}
            {s.label}
          </Button>
        ))}
      </div>
    </div>
  );
}
